import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { requireSuperAdminGuard } from "@/lib/security/tenantIsolation";
import { runPredictionEngineTickFn, getEngineStatsFn } from "@/lib/predictions/predictionEngine.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Activity, Trophy, Users, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { NationWarsLeaderboard } from "@/components/nationwars/NationWarsLeaderboard";
import { ViralSharesAdminCard } from "@/components/viral/ViralSharesAdminCard";
import { FanDataPulseAdminCard } from "@/components/admin/FanDataPulseAdminCard";
import { MatchPulseAdminCard } from "@/components/admin/MatchPulseAdminCard";

export const Route = createFileRoute("/admin/prediction-engine")({
  beforeLoad: ({ location }) => { requireSuperAdminGuard(location.pathname); },
  head: () => ({
    meta: [
      { title: "Prediction Engine Monitor — VerseSport Admin" },
      { name: "description", content: "Mundial Prediction Engine — observability & manual tick." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PredictionEngineMonitor,
});

function PredictionEngineMonitor() {
  const stats = useServerFn(getEngineStatsFn);
  const tick = useServerFn(runPredictionEngineTickFn);
  const [fixtureId, setFixtureId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [running, setRunning] = useState(false);
  const [lastTick, setLastTick] = useState<unknown>(null);

  const q = useQuery({
    queryKey: ["prediction-engine-stats"],
    queryFn: () => stats(),
    refetchInterval: 10_000,
  });

  async function runTick() {
    if (!fixtureId || !matchId) return;
    setRunning(true);
    try {
      const res = await tick({ data: { fixtureId, matchId } });
      setLastTick(res);
      void q.refetch();
    } catch (e) {
      setLastTick({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      setRunning(false);
    }
  }

  const s = q.data;

  return (
    <main className="container mx-auto p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Prediction Engine Monitor</h1>
          <p className="text-sm text-muted-foreground">Mundial™ — Fase 2 Automation</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${q.isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Activity className="h-4 w-4" />} label="Eventos creados (24h)" value={s?.events_created_24h ?? "—"} />
        <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Eventos resueltos (24h)" value={s?.events_resolved_24h ?? "—"} />
        <StatCard icon={<Users className="h-4 w-4" />} label="Usuarios (24h)" value={s?.users_participating_24h ?? "—"} />
        <StatCard icon={<Trophy className="h-4 w-4" />} label="XP otorgado (24h)" value={s?.xp_awarded_24h ?? "—"} />
        <StatCard icon={<Activity className="h-4 w-4" />} label="Abiertos ahora" value={s?.open_now ?? "—"} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Clock className="h-4 w-4" />} label="Último tick" value={s?.ticks.last_tick_at ? new Date(s.ticks.last_tick_at).toLocaleTimeString() : "—"} />
        <StatCard icon={<Clock className="h-4 w-4" />} label="Duración último (ms)" value={s?.ticks.last_duration_ms ?? "—"} />
        <StatCard icon={<Activity className="h-4 w-4" />} label="Ticks (24h)" value={s?.ticks.ticks_24h ?? "—"} />
        <StatCard icon={<Clock className="h-4 w-4" />} label="Duración media (ms)" value={s?.ticks.avg_duration_ms ?? "—"} />
        <StatCard icon={<AlertTriangle className="h-4 w-4" />} label="Errores (24h)" value={s?.ticks.errors_24h ?? "—"} />
      </div>

      <Card>
        <CardHeader><CardTitle>Eventos creados por tipo (24h)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s?.ticks.events_by_type_24h ?? {}).map(([k, v]) => (
              <Badge key={k} variant="outline">{k}: {v}</Badge>
            ))}
            {!s?.ticks.events_by_type_24h || Object.keys(s.ticks.events_by_type_24h).length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin actividad.</p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Manual Tick</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="API-Football fixture id (ej: 1234567)" value={fixtureId} onChange={(e) => setFixtureId(e.target.value)} />
            <Input placeholder="Internal match_id" value={matchId} onChange={(e) => setMatchId(e.target.value)} />
            <Button onClick={runTick} disabled={running || !fixtureId || !matchId}>
              {running ? "Procesando…" : "Ejecutar tick"}
            </Button>
          </div>
          {lastTick !== null && (
            <pre className="text-xs bg-muted/40 p-3 rounded overflow-auto max-h-72">{JSON.stringify(lastTick, null, 2)}</pre>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Últimos eventos</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(s?.recent ?? []).map((r) => (
              <div key={r.id} className="flex items-center justify-between border border-border/60 rounded px-3 py-2 text-sm">
                <div className="flex flex-col">
                  <span className="font-medium">{r.question}</span>
                  <span className="text-xs text-muted-foreground">match {r.match_id} · {r.event_type} · {new Date(r.created_at).toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  {r.correct_answer && <Badge variant="outline">→ {r.correct_answer}</Badge>}
                  <Badge variant={r.status === "resolved" ? "default" : r.status === "open" ? "secondary" : "outline"}>{r.status}</Badge>
                </div>
              </div>
            ))}
            {!s?.recent?.length && <p className="text-sm text-muted-foreground">Sin actividad reciente.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Tick log</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(s?.ticks.recent ?? []).map((t) => (
              <div key={t.id} className="flex items-center justify-between border border-border/60 rounded px-3 py-2 text-sm">
                <div className="flex flex-col">
                  <span className="font-medium">{new Date(t.started_at).toLocaleString()} · {t.duration_ms}ms</span>
                  <span className="text-xs text-muted-foreground">fixture {t.fixture_id ?? "—"} · match {t.match_id ?? "—"} · +{t.events_created} / ✓{t.events_resolved} · XP {t.xp_awarded}</span>
                </div>
                {t.error ? <Badge variant="destructive">error</Badge> : <Badge variant="secondary">ok</Badge>}
              </div>
            ))}
            {!s?.ticks.recent?.length && <p className="text-sm text-muted-foreground">Sin ticks registrados.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Nation Wars — Top 10</CardTitle>
        </CardHeader>
        <CardContent>
          <NationWarsLeaderboard limit={10} compact />
        </CardContent>
      </Card>

      <ViralSharesAdminCard />

      <FanDataPulseAdminCard />

      <MatchPulseAdminCard />
    </main>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
        <div className="text-2xl font-bold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}